import type {
  PortfolioRead as ApiPortfolio,
  PortfolioPreview as ApiPortfolioPreview,
  PortfolioSummary as ApiPortfolioSummary,
} from "@mm/metx-workbench.meteomatics.com";

export enum PortfolioType {
  Wind = "wind",
  Solar = "solar",
}

export enum PortfolioDataSource {
  Energy = "energy",
  Api = "api",
}
export type PortfolioRow = {
  id?: string;
  assetId?: string | null;
  assetName?: string | null;
  coordinates: { lat: unknown; lon: unknown };
  properties?: Record<string, unknown>;
  turbineId?: string | null;
  hubHeight?: unknown;
  specifications?: {
    installedCapacityKw?: unknown;
    trackingType?: string | null;
    tiltDeg?: unknown;
    orientationDeg?: unknown;
    criticalSnowdepthCm?: unknown;
  };
};
type WithPortfolioRows<T extends object> = Omit<
  T,
  "assets" | "portfolio_type" | "data_source" | "id_account" | "asset_count" | "skipped_empty_rows"
> & {
  assets: PortfolioRow[];
  portfolioType: PortfolioType;
  dataSource?: PortfolioDataSource;
  idAccount?: number;
  assetCount?: number;
  skippedEmptyRows?: number;
};

export type Portfolio = WithPortfolioRows<ApiPortfolio>;
export type PortfolioPreview = WithPortfolioRows<ApiPortfolioPreview>;
export type PortfolioSummary = Omit<
  ApiPortfolioSummary,
  "portfolio_type" | "data_source" | "id_account" | "asset_count"
> & {
  portfolioType: PortfolioType;
  dataSource: PortfolioDataSource;
  idAccount: number;
  assetCount: number;
};
export type AssetRef = { portfolioId: string; assetId: string };

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asStringOrNull(value: unknown): string | null | undefined {
  return typeof value === "string" || value === null ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function portfolioRowFromApi(asset: unknown): PortfolioRow {
  const source = asRecord(asset);
  const coordinates = asRecord(source.coordinates);
  const specifications = asRecord(source.specifications);
  const properties = asRecord(source.properties);
  return {
    id: asString(source.id),
    assetId: asStringOrNull(source.asset_id),
    // Some legacy portfolio imports retain their display name in the original
    // CSV `Asset` column rather than the normalized `asset_name` field.
    assetName:
      asString(source.asset_name) ??
      asString(source.Asset) ??
      asString(properties.Asset) ??
      asStringOrNull(source.asset_name),
    coordinates: { lat: coordinates.lat, lon: coordinates.lon },
    properties,
    turbineId: asStringOrNull(source.turbine_id),
    hubHeight: source.hub_height,
    specifications: {
      ...("installed_capacity_kw" in specifications
        ? { installedCapacityKw: specifications.installed_capacity_kw }
        : {}),
      ...("tracking_type" in specifications ? { trackingType: asStringOrNull(specifications.tracking_type) } : {}),
      ...("tilt_deg" in specifications ? { tiltDeg: specifications.tilt_deg } : {}),
      ...("orientation_deg" in specifications ? { orientationDeg: specifications.orientation_deg } : {}),
      ...("critical_snowdepth_cm" in specifications
        ? { criticalSnowdepthCm: specifications.critical_snowdepth_cm }
        : {}),
    },
  };
}

export function portfolioFromApi(portfolio: ApiPortfolio): Portfolio {
  return {
    id: portfolio.id,
    name: portfolio.name,
    idAccount: portfolio.id_account,
    portfolioType: portfolio.portfolio_type as PortfolioType,
    dataSource: portfolio.data_source as PortfolioDataSource,
    assets: portfolio.assets.map(portfolioRowFromApi),
  };
}

export function portfolioPreviewFromApi(preview: ApiPortfolioPreview): PortfolioPreview {
  return {
    portfolioType: preview.portfolio_type as PortfolioType,
    dataSource: preview.data_source as PortfolioDataSource | undefined,
    assetCount: preview.asset_count,
    skippedEmptyRows: preview.skipped_empty_rows,
    assets: (preview.assets ?? []).map(portfolioRowFromApi),
    errors: preview.errors,
  };
}

export function portfolioSummaryFromApi(portfolio: ApiPortfolioSummary): PortfolioSummary {
  return {
    id: portfolio.id,
    name: portfolio.name,
    idAccount: portfolio.id_account,
    portfolioType: portfolio.portfolio_type as PortfolioType,
    dataSource: portfolio.data_source as PortfolioDataSource,
    assetCount: portfolio.asset_count,
  };
}

export function hasRequiredCoordinates(
  asset: PortfolioRow,
): asset is PortfolioRow & { coordinates: { lat: number; lon: number } } {
  return (
    typeof asset.coordinates?.lat === "number" &&
    Number.isFinite(asset.coordinates.lat) &&
    asset.coordinates.lat >= -90 &&
    asset.coordinates.lat <= 90 &&
    typeof asset.coordinates?.lon === "number" &&
    Number.isFinite(asset.coordinates.lon) &&
    asset.coordinates.lon >= -180 &&
    asset.coordinates.lon <= 180
  );
}

export function hasRequiredEnergyFields(asset: PortfolioRow) {
  const siteId = asset.properties?.site_id;
  return typeof siteId === "string" && siteId.trim().length > 0;
}

export function resolveAsset(portfolio: Portfolio | undefined, reference: AssetRef) {
  if (portfolio?.id !== reference.portfolioId) {
    return undefined;
  }
  const asset = portfolio.assets.find((asset) => asset.id === reference.assetId);
  return asset ? { portfolioType: portfolio.portfolioType, asset } : undefined;
}

export function buildPowerParameter(portfolio: Portfolio, assetId: string, unit: "kW" | "MW") {
  const result = resolveAsset(portfolio, { portfolioId: portfolio.id, assetId });
  if (!result) {
    throw new Error("Asset not found in portfolio");
  }
  return buildAssetPowerParameter(result.portfolioType, result.asset, unit);
}

export function buildAssetPowerParameter(portfolioType: PortfolioType, asset: PortfolioRow, unit: "kW" | "MW") {
  if (portfolioType === PortfolioType.Wind) {
    return `wind_power_turbine_${asset.turbineId}_hub_height_${asset.hubHeight}m:${unit}`;
  }
  const specs = asset.specifications;
  const parts: string[] = [];
  if (typeof specs?.installedCapacityKw === "number") {
    parts.push(`installed_capacity_${specs.installedCapacityKw / 1000}`);
  }
  if (specs?.trackingType != null) {
    parts.push(`tracking_type_${specs.trackingType}`);
  }
  if (typeof specs?.tiltDeg === "number") {
    parts.push(`tilt_${specs.tiltDeg}`);
  }
  if (typeof specs?.orientationDeg === "number") {
    parts.push(`orientation_${specs.orientationDeg}`);
  }
  if (typeof specs?.criticalSnowdepthCm === "number") {
    parts.push(`critical_snowdepth_${specs.criticalSnowdepthCm}`);
  }
  return `solar_power${parts.length ? `_${parts.join("_")}` : ""}:${unit}`;
}
